/**
 * Hard local-state reset (#1964).
 *
 * Nukes EVERY byte of PWA-owned local storage so the next load is a
 * brand-new install. Used by the admin-only "Hard Reset Day" flow on
 * Profile after the server-side void of the rep's open CallLogs.
 *
 * Without the server-side void this is futile: the next page load
 * re-hydrates from server and reconstitutes whatever stuck state
 * triggered the reset. See apps/projects/apis/admin.py and the
 * Profile button for the full two-part flow.
 *
 * This intentionally does NOT preserve tokens or impersonation — the
 * post-reset experience is a fresh login. That's the whole point.
 */

import { closeDatabase, clearAllStores } from './indexedDB';

export interface HardResetResult {
  indexedDBs: string[];
  clearedStores: string[];
  localStorageKeys: number;
  sessionStorageKeys: number;
  caches: string[];
  serviceWorkers: number;
  errors: string[];
}

export async function hardResetLocalState(): Promise<HardResetResult> {
  const result: HardResetResult = {
    indexedDBs: [],
    clearedStores: [],
    localStorageKeys: 0,
    sessionStorageKeys: 0,
    caches: [],
    serviceWorkers: 0,
    errors: [],
  };

  // 1. localStorage + sessionStorage (synchronous, safe)
  try {
    result.localStorageKeys = localStorage.length;
    localStorage.clear();
  } catch (err) {
    result.errors.push(`localStorage.clear: ${err instanceof Error ? err.message : String(err)}`);
  }
  try {
    result.sessionStorageKeys = sessionStorage.length;
    sessionStorage.clear();
  } catch (err) {
    result.errors.push(`sessionStorage.clear: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 2. IndexedDB — best-effort enumerate + delete every DB.
  // indexedDB.databases() is supported in Chrome/Safari/Edge but NOT
  // older Firefox; the fallback hits our known DB name explicitly.
  //
  // CRITICAL: close our cached connection first. Without this, the
  // deleteDatabase request hits 'blocked' on iOS Safari, the pending
  // delete never completes before window.location.replace fires, and
  // the post-reload reads the stale day session back as if nothing
  // was nuked. See indexedDB.ts closeDatabase + onversionchange.
  closeDatabase();

  // Reliable wipe FIRST: clear every store's contents. deleteDatabase below is
  // best-effort — on iOS Safari it blocks while the sync engine holds a
  // connection and silently times out, leaving stuck day/session/queue rows
  // behind (the exact failure in diag #43). Clearing stores succeeds with live
  // connections, so the data is actually gone even if the delete can't finish.
  try {
    result.clearedStores = await clearAllStores();
  } catch (err) {
    result.errors.push(`clearAllStores: ${err instanceof Error ? err.message : String(err)}`);
  }

  try {
    let names: string[] = [];
    if (typeof indexedDB.databases === 'function') {
      const dbs = await indexedDB.databases();
      names = dbs.map((d) => d.name).filter((n): n is string => !!n);
    }
    // Always include our known DB name even if enumeration found it —
    // dedup via Set so we don't double-delete.
    names = Array.from(new Set([...names, 'rdm_app_offline']));
    await Promise.all(
      names.map(
        (name) =>
          new Promise<void>((resolve) => {
            const req = indexedDB.deleteDatabase(name);
            // Bound the wait so a stuck deletion (e.g. another open
            // tab refuses to close) can't lock the modal in 'Resetting…'
            // forever. After the timeout we proceed; the navigation +
            // SW unregister will still get us most of the way.
            const timer = window.setTimeout(() => {
              result.errors.push(`deleteDatabase(${name}) timed out after 5s`);
              resolve();
            }, 5000);
            req.onsuccess = () => {
              window.clearTimeout(timer);
              resolve();
            };
            req.onerror = () => {
              window.clearTimeout(timer);
              result.errors.push(`deleteDatabase(${name}) errored`);
              resolve();
            };
            // 'blocked' fires when another connection still holds the
            // DB open. We don't resolve here — wait for the actual
            // deletion to complete (or the timeout above to fire).
            // Without waiting, the navigation below races the deletion
            // and the next load sees stale data.
            req.onblocked = () => {
              result.errors.push(`deleteDatabase(${name}) blocked — waiting for other connections to close`);
            };
          }),
      ),
    );
    result.indexedDBs = names;
  } catch (err) {
    result.errors.push(`indexedDB: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 3. Cache Storage — every PWA cache (precache, runtime, etc.)
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
      result.caches = keys;
    }
  } catch (err) {
    result.errors.push(`caches: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 4. Service Worker — unregister so the next load fetches a fresh
  // bundle from the network rather than the SW's precache.
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      result.serviceWorkers = regs.length;
    }
  } catch (err) {
    result.errors.push(`serviceWorker: ${err instanceof Error ? err.message : String(err)}`);
  }

  return result;
}
